import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-[#D99A29]/15 text-[#8a6414]",
  approved: "bg-[#A9C97E]/10 text-[#A9C97E]",
  declined: "bg-[#F87171]/10 text-[#F87171]",
};

export default function Leave() {
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [deciding, setDeciding] = useState(null); // {req, status}
  const [paid, setPaid] = useState(true);

  const load = () => {
    api.get("/leave").then((r) => setRequests(r.data)).catch(() => toast.error("Failed to load leave"));
    api.get("/leave/balances/all").then((r) => setBalances(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const decide = async () => {
    try {
      await api.post(`/leave/${deciding.req.id}/decide`, { status: deciding.status, paid });
      toast.success(`Leave ${deciding.status}`);
      setDeciding(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    }
  };

  const table = (items) => (
    <div className="glass border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Employee</th>
            <th className="px-4 py-3 font-medium">Type</th>
            <th className="px-4 py-3 font-medium">Dates</th>
            <th className="px-4 py-3 font-medium text-right">Days</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Reason</th>
            <th className="px-4 py-3 font-medium"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-b last:border-0" data-testid={`leave-row-${r.id}`}>
              <td className="px-4 py-3 font-medium">{r.employee_name}</td>
              <td className="px-4 py-3 capitalize">{r.type}</td>
              <td className="px-4 py-3">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</td>
              <td className="px-4 py-3 num">{r.days}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[r.status]}`}>
                  {r.status}{r.status === "approved" && !r.paid ? " (unpaid)" : ""}
                </span>
              </td>
              <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate">{r.reason || "—"}</td>
              <td className="px-4 py-3">
                {r.status === "pending" && (
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" data-testid={`approve-${r.id}`} onClick={() => { setPaid(true); setDeciding({ req: r, status: "approved" }); }}
                      className="bg-primary hover:bg-[#8B5CF6] text-white">Approve</Button>
                    <Button size="sm" variant="outline" data-testid={`decline-${r.id}`} onClick={() => setDeciding({ req: r, status: "declined" })}>Decline</Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No requests.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="leave-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Leave</h1>
        <p className="text-muted-foreground mt-1">Accrual: 1.25 days per calendar month worked</p>
      </div>
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({requests.filter((r) => r.status === "pending").length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All requests</TabsTrigger>
          <TabsTrigger value="balances" data-testid="tab-balances">Balances</TabsTrigger>
        </TabsList>
        <TabsContent value="pending">{table(requests.filter((r) => r.status === "pending"))}</TabsContent>
        <TabsContent value="all">{table(requests)}</TabsContent>
        <TabsContent value="balances">
          <div className="glass border border-border rounded-lg overflow-x-auto" data-testid="leave-balances">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Employee</th>
                  <th className="px-4 py-3 font-medium">Position</th>
                  <th className="px-4 py-3 font-medium text-right">Accrued</th>
                  <th className="px-4 py-3 font-medium text-right">Taken</th>
                  <th className="px-4 py-3 font-medium text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => (
                  <tr key={b.employee_id} className="border-b last:border-0" data-testid={`balance-${b.employee_id}`}>
                    <td className="px-4 py-3 font-medium">{b.name}</td>
                    <td className="px-4 py-3">{b.position}</td>
                    <td className="px-4 py-3 num">{b.accrued}</td>
                    <td className="px-4 py-3 num">{b.taken}</td>
                    <td className="px-4 py-3 num font-semibold">{b.balance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!deciding} onOpenChange={() => setDeciding(null)}>
        <DialogContent className="max-w-sm" data-testid="leave-decide-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading capitalize">{deciding?.status} leave</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deciding?.req.employee_name} · {deciding?.req.type} · {deciding?.req.days} day(s)
          </p>
          {deciding?.status === "approved" && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox data-testid="leave-paid-checkbox" checked={paid} onCheckedChange={(v) => setPaid(!!v)} />
              Paid leave (untick = unpaid, deducted in payroll)
            </label>
          )}
          <Button onClick={decide} data-testid="leave-decide-confirm" className="bg-primary hover:bg-[#8B5CF6] text-white w-full">
            Confirm {deciding?.status}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
